import { useEffect, useState } from "react";
import FilterTags from "./FilterTags";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_LID = { name: "", tags: [], story: "", athleteId: null };

function AthleteSinglePicker({ selected, csrfToken, onChange }) {
  const [allAthletes, setAllAthletes] = useState([]);
  const [query, setQuery] = useState("");
  const [lookup, setLookup] = useState({ value: "", result: null, error: "" });
  const [open, setOpen] = useState(!selected);

  useEffect(() => {
    fetch(`${API}/athletes`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => setAllAthletes(Array.isArray(data) ? data : []));
  }, []);

  const filtered = query.trim()
    ? allAthletes.filter(a =>
        (a.displayName || a.name || "").toLowerCase().includes(query.toLowerCase())
      )
    : allAthletes;

  const select = (athlete) => {
    onChange(athlete);
    setOpen(false);
    setQuery("");
  };

  const doLookup = async () => {
    const q = lookup.value.trim();
    if (!q) return;
    try {
      const res = await fetch(`${API}/admin/athlete-lookup?q=${encodeURIComponent(q)}`, {
        credentials: "include",
        headers: { "CSRF-Token": csrfToken }
      });
      if (!res.ok) {
        const data = await res.json();
        setLookup(l => ({ ...l, result: null, error: data.error || "Niet gevonden" }));
        return;
      }
      const athlete = await res.json();
      setLookup(l => ({ ...l, result: athlete, error: "" }));
    } catch {
      setLookup(l => ({ ...l, result: null, error: "Lookup mislukt" }));
    }
  };

  return (
    <div className="athlete-picker">
      {selected && (
        <div className="athlete-picker-selected">
          <div className="athlete-picker-tag">
            {selected.profilePhoto && <img src={selected.profilePhoto} alt="" className="leaderboard-photo" />}
            <span>{selected.displayName || selected.name}</span>
            <button type="button" className="btn-secondary btn-xs" onClick={() => { onChange(null); setOpen(true); }}>
              Wijzigen
            </button>
          </div>
        </div>
      )}

      {open && (
        <>
          <div className="athlete-picker-search">
            <label>Zoek op naam</label>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Naam deelnemer…"
              className="athlete-picker-input"
              autoFocus
            />
            <div className="athlete-picker-list">
              {filtered.slice(0, 10).map(a => (
                <div key={a._id} className="athlete-picker-option" onClick={() => select(a)}>
                  {a.profilePhoto && <img src={a.profilePhoto} alt="" className="leaderboard-photo" />}
                  {a.displayName || a.name}
                </div>
              ))}
              {filtered.length === 0 && query && <p className="note">Geen deelnemers gevonden.</p>}
            </div>
          </div>

          <div className="athlete-picker-id">
            <label>Of voeg toe via Mongo _id of Strava ID</label>
            <div className="athlete-picker-id-row">
              <input
                value={lookup.value}
                onChange={e => setLookup(l => ({ ...l, value: e.target.value, result: null, error: "" }))}
                placeholder="bijv. 683abc… of 12345678"
                className="athlete-picker-input"
                onKeyDown={e => e.key === "Enter" && doLookup()}
              />
              <button type="button" className="btn-secondary" onClick={doLookup}>Zoeken</button>
            </div>
            {lookup.error && <p className="content-error">{lookup.error}</p>}
            {lookup.result && (
              <div className="athlete-picker-option" onClick={() => { select(lookup.result); setLookup({ value: "", result: null, error: "" }); }}>
                {lookup.result.profilePhoto && <img src={lookup.result.profilePhoto} alt="" className="leaderboard-photo" />}
                {lookup.result.displayName || lookup.result.name}
                <span className="note"> — klik om te selecteren</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function LidEditor({ lid, csrfToken, onSaved, onDeleted, onCancel }) {
  const [form, setForm] = useState({
    name: lid.name || "",
    tags: lid.tags || [],
    story: lid.story || "",
    athleteId: lid.athleteId || null
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const isNew = !lid._id;
  const hasAthlete = !!form.athleteId;
  const displayName = hasAthlete
    ? (form.athleteId.displayName || form.athleteId.name)
    : form.name;

  const save = async () => {
    if (!hasAthlete && !form.name.trim()) { setError("Vul een naam in of selecteer een deelnemer"); return; }
    setSaving(true);
    setError("");
    const body = JSON.stringify({
      name: hasAthlete ? "" : form.name,
      tags: form.tags,
      story: form.story,
      athleteId: hasAthlete ? form.athleteId._id : null
    });
    const res = await fetch(
      isNew ? `${API}/admin/organizations` : `${API}/admin/organizations/${lid._id}`,
      {
        method: isNew ? "POST" : "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", "CSRF-Token": csrfToken },
        body
      }
    );
    setSaving(false);
    if (!res.ok) {
      const data = await res.json();
      setError(data.error || "Opslaan mislukt");
      return;
    }
    const saved = await res.json();
    onSaved({ ...saved, athleteId: form.athleteId }, isNew);
  };

  const remove = async () => {
    if (!window.confirm(`Lid "${displayName || "dit lid"}" verwijderen?`)) return;
    await fetch(`${API}/admin/organizations/${lid._id}`, {
      method: "DELETE",
      credentials: "include",
      headers: { "CSRF-Token": csrfToken }
    });
    onDeleted(lid._id);
  };

  return (
    <div className="content-editor">
      <label>Deelnemer (optioneel)</label>
      <AthleteSinglePicker
        selected={form.athleteId}
        csrfToken={csrfToken}
        onChange={athlete => setForm(f => ({ ...f, athleteId: athlete }))}
      />

      {!hasAthlete && (
        <div className="content-editor-field">
          <label>Naam</label>
          <input
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="bijv. Jan de Vries"
          />
        </div>
      )}

      <label>Persoonlijke info (bijv. beroep, hobby)</label>
      <FilterTags
        filters={form.tags}
        onChange={tags => setForm(f => ({ ...f, tags }))}
        placeholder="bijv. software developer"
      />

      <label>Verhaal / motivatie (kort)</label>
      <textarea
        className="content-main-body"
        value={form.story}
        onChange={e => setForm(f => ({ ...f, story: e.target.value }))}
        placeholder="Waarom doe je mee? Wat drijft je?"
      />

      {error && <p className="content-error">{error}</p>}

      <div className="content-editor-actions">
        <button onClick={save} disabled={saving}>{saving ? "Opslaan…" : "Opslaan"}</button>
        <button className="btn-secondary" onClick={onCancel}>Annuleren</button>
        {!isNew && <button className="btn-danger" onClick={remove}>Verwijderen</button>}
      </div>
    </div>
  );
}

export default function AdminOrganizations({ csrfToken }) {
  const [leden, setLeden] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    fetch(`${API}/organizations`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => setLeden(Array.isArray(data) ? data : []));
  }, []);

  const handleSaved = (saved, isNew) => {
    if (isNew) {
      setLeden(prev => [...prev, saved]);
    } else {
      setLeden(prev => prev.map(l => l._id === saved._id ? saved : l));
    }
    setEditing(null);
  };

  const handleDeleted = (id) => {
    setLeden(prev => prev.filter(l => l._id !== id));
    setEditing(null);
  };

  if (editing !== null) {
    return (
      <LidEditor
        lid={editing}
        csrfToken={csrfToken}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div>
      <div className="content-list-header">
        <h2>Leden</h2>
        <button onClick={() => setEditing({ ...EMPTY_LID })}>+ Nieuw lid</button>
      </div>
      <div className="content-list">
        {leden.length === 0 && <p>Nog geen leden.</p>}
        {leden.map(lid => {
          const athlete = lid.athleteId;
          return (
            <div key={lid._id} className="content-list-item">
              <span>
                {athlete?.profilePhoto && (
                  <img src={athlete.profilePhoto} alt="" className="leaderboard-photo" />
                )}
                <strong>{athlete?.displayName || athlete?.name || "—"}</strong>
                {lid.story && <small> — {lid.story.slice(0, 60)}{lid.story.length > 60 ? "…" : ""}</small>}
              </span>
              <button className="btn-secondary" onClick={() => setEditing(lid)}>Bewerken</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
